import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDeviceType, useIntersectionObserver } from '@vfit/shared/hooks';
import {
  errorManager,
  ErrorService,
  trackLink,
  getClickToCall,
  linkInAppOpener,
} from '@vfit/shared/data-access';
import {
  ButtonCBU,
  CoverageToolModal,
  ISupportModule,
  ProductDetail,
} from '@vfit/consumer/components';
import { CustomText } from '@vfit/shared/atoms';
import {
  removeCurrency,
  usePartnerOfferFlow,
  useRedirectCoverageToolFlow,
} from '@vfit/consumer/hooks';
import {
  checkPanicMode,
  CONSUMER_CMS_DEFAULT,
  getFormattedPriceString,
  getProductCategory,
  getRedirectCoverage,
  IGlobalConfigCMS,
  IN_ACTIVATION,
  IProduct,
  isEnergyProduct,
  IWinBackCampaignConfig,
  IWinBackCMS,
  redirectToShoppingCartFromReverseAddress,
  SKIP_OLD_PARTNEROFFER,
  TOKEN_PARTNER_OFFER,
  useCmsConfig,
  usePage,
} from '@vfit/consumer/data-access';
import { StickyOffer, VfModal } from '@vfit/shared/components';
import { fonts } from '@vfit/shared/themes';
import {
  IActionSlide,
  IActionType,
  IErrorFormApi,
  ISubmitOutput,
  IType,
} from '@vfit/shared/models';
import {
  API,
  IGetAllCmsForm,
  manageErrorLeadPlatform,
  manageSuccessLeadPlatform,
  openLeadModal,
} from '@vfit/shared/data-access';
import { useQueryClient } from 'react-query';
import { energyManager, SoftManagerService } from '@vfit/consumer/providers';
import { useRouter } from 'next/router';
import {
  ModalButtonContainer,
  ModalTitle,
  ModalContent,
  StickyOfferContainerTopProduct,
} from './topProduct.style';
import { IProductModal, ITopProductProps } from './topProduct.models';
import { checkProductOfferFlow } from './topProduct.utils';
import TopNavigation from './components/TopNavigation/topNavigation';
import TopNavigationLite from './components/TopNavigationLite/topNavigationLite';

const TopProduct = (props: ITopProductProps) => {
  const { product: topProduct, isR02, onOverrideCoverageToolCta } = props || {};
  const { push } = useRouter();
  const { cmsId } = topProduct;
  const queryClient = useQueryClient();
  const { page, silentLoginUser } = usePage();
  const winbackCampaignsList = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_WINBACK_CONFIG
  ) as IWinBackCampaignConfig;
  const cmsWinbackData = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_WINBACK_DATA
  ) as IWinBackCMS;
  const { globalconfig } =
    (useCmsConfig(CONSUMER_CMS_DEFAULT, API.CMS_GET_GLOBAL_CONFIG) as IGlobalConfigCMS) || {};
  const { panicMode } = globalconfig || {};
  const queryString = new URLSearchParams(window?.location?.search);
  const skip = queryString?.get(SKIP_OLD_PARTNEROFFER) === 'true';

  const { isSuccessPartnerOffer } = usePartnerOfferFlow({ product: topProduct, enabler: !skip });

  const partnerValidateVMS = queryClient.getQueryData('partnershipValidate');

  const product: IProduct = checkProductOfferFlow(topProduct, winbackCampaignsList, cmsWinbackData);

  const [isShowCoverageTool, setIsShowCoverageTool] = useState(false);
  const [directionSlideStickyOffer, setDirectionSlideStickyOffer] = useState<
    'upward' | 'downward' | undefined
  >();
  const [isInActivation] = useRedirectCoverageToolFlow(cmsId || '');
  const [modal, setModal] = useState<IProductModal>({
    show: false,
    type: IType.TEXT,
  });
  const supportModulesCms = useCmsConfig(
    CONSUMER_CMS_DEFAULT,
    API.CMS_GET_SUPPORT_MODULE
  ) as ISupportModule;
  const slideProductMainBtn = useRef<HTMLDivElement>(null);
  const { isIntersecting: slideProductMainBtnIsIntersected } =
    useIntersectionObserver(slideProductMainBtn, { rootMargin: '-10%' }) || {};
  const { isDesktop } = useDeviceType();

  const productOfferTitle = getFormattedPriceString(product.price, product.recurrence);
  const detailLabel = product?.buttons?.[0]?.label || product?.buttonShow || '';
  const buttonAction = product?.action;

  useEffect(() => {
    if (!isShowCoverageTool && isInActivation) {
      setIsShowCoverageTool(true);
    }
  }, [isInActivation]);

  const resetModal = () => {
    setModal({
      show: false,
      type: IType.TEXT,
      text: '',
      title: '',
      disableOrganizeText: false,
    });
  };

  const onActionProduct = (action?: IActionSlide) => {
    let allLeads;
    let refLead;
    let lead;
    let pstEnrgEvent;
    trackLink(action?.title || '');
    switch (action?.type) {
      case IActionType.CUSTOM_URL:
        if (action?.url) {
          linkInAppOpener(action.url, action?.isBlank === 'true' ? '_blank' : '_self');
        }
        break;
      case IActionType.CALL_ME_NOW:
        getClickToCall(queryClient, {
          product,
          idClickToCall: action?.key,
          clickToCallFallbackUrl: action?.callMeNow || action?.url,
        });
        break;
      case IActionType.CALL_ME_NOW_SUNRISE:
        if (!action?.title || !action?.url) return;
        getClickToCall(queryClient, {
          product,
          idClickToCall: action?.key,
          clickToCallFallbackUrl: action?.callMeNow || action?.url,
          clickToCallFallbackType: 'CTC_SUNRISE',
          clickToCallFallbackTemplate: action?.template,
        });
        break;
      case IActionType.COVERAGE_TOOL:
        if (onOverrideCoverageToolCta) onOverrideCoverageToolCta();
        else setIsShowCoverageTool(true);
        break;
      case IActionType.CHECKOUT_MODAL_MOBILE:
        localStorage.setItem('tProd', JSON.stringify(product));
        window.location.href = getRedirectCoverage();
        break;
      case IActionType.COVERAGE_TOOL_WITH_REDIRECT:
        if (action?.url && cmsId) {
          const alreadyHaveParams = action.url.includes('?');
          const redirectUrl = alreadyHaveParams
            ? `${action.url}&${IN_ACTIVATION}=${cmsId}`
            : `${action.url}?${IN_ACTIVATION}=${cmsId}`;
          window.location.href = redirectUrl;
        }
        break;
      case IActionType.LEAD_MODAL:
        allLeads = queryClient?.getQueryData('getAllLeads') as IGetAllCmsForm;
        // form name
        refLead = action?.localUrl || '';
        lead = allLeads[refLead];
        if (lead?.forms) {
          openLeadModal({
            form: lead.forms,
            onSuccess: (submitOutput: ISubmitOutput) =>
              manageSuccessLeadPlatform(submitOutput, push, queryClient),
            onError: (submitOutput: ISubmitOutput[], errorCmsApi?: IErrorFormApi) =>
              manageErrorLeadPlatform(submitOutput, errorCmsApi, push, queryClient),
          });
        }
        break;
      case IActionType.CLICK_TO_CALL_ENERGY:
        document.dispatchEvent(
          new CustomEvent('energyCtcNotificationEvent', {
            detail: {
              product,
              action: action,
              taggingOption: page?.params?.elements?.taggingOptions,
            },
          })
        );
        break;
      case IActionType.POST_ENRG:
        trackLink(action?.title || '');
        pstEnrgEvent = new CustomEvent('enrgServiceNotificationEvent', {
          detail: {
            nof: action?.localUrl,
          },
        });
        document.dispatchEvent(pstEnrgEvent);
        break;
      case IActionType.AUTO_COVERAGE:
        if (cmsId) redirectToShoppingCartFromReverseAddress(cmsId);
        break;
      case IActionType.ENERGY_FUNNEL:
        energyManager.handleEnergyManager({
          product,
          showModal: true,
        });
        break;
      default:
        break;
    }
  };

  const hideCoverageTool = () => {
    setIsShowCoverageTool(false);
  };

  const modalCustomFooter = () => (
    <ModalButtonContainer>
      <ButtonCBU
        variant={'primary'}
        onClick={() => {
          resetModal();
          onActionProduct(
            checkPanicMode(panicMode, product) ? product.panicModeAction : buttonAction
          );
        }}
        name={`action_top_product_${product?.offerId?.toString() || ''}`}
      >
        {checkPanicMode(panicMode, product) ? product.panicModeAction?.title : buttonAction?.title}
      </ButtonCBU>
    </ModalButtonContainer>
  );

  const modalSimpleText = () => (
    <>
      {modal.title && (
        <ModalTitle>
          <CustomText
            fontFamily={fonts.exbold}
            size={36}
            lineHeight={45}
            textAlign="left"
            text={modal.title}
          />
        </ModalTitle>
      )}
      {modal.text && (
        <ModalContent>
          <CustomText
            size={18}
            lineHeight={24}
            textAlign="left"
            text={modal.text}
            disableOrganizeText={modal.disableOrganizeText}
          />
        </ModalContent>
      )}
    </>
  );

  const checkIsLoadingActivation = () => {
    return buttonAction?.type === IActionType.ENERGY_FUNNEL && !silentLoginUser?.isReady;
  };

  const getPDPMainAction = () => {
    return {
      action: () =>
        onActionProduct(
          checkPanicMode(panicMode, product) ? product.panicModeAction : buttonAction
        ),
      label:
        (checkPanicMode(panicMode, product)
          ? product.panicModeAction?.title
          : buttonAction?.title) || '',
    };
  };

  const getPDPSecondAction = () => {
    if (checkPanicMode(panicMode, product)) return undefined;
    if (isEnergyProduct(product) && checkPanicMode(panicMode, product)) return undefined;
    return {
      action: () => {
        onActionProduct(product.pdpSecondAction);
      },
      label: product?.pdpSecondAction?.title || '',
    };
  };

  useLayoutEffect(() => {
    if (isDesktop) {
      setDirectionSlideStickyOffer(slideProductMainBtnIsIntersected ? 'downward' : 'upward');
    }
  }, [isDesktop, slideProductMainBtnIsIntersected]);

  if (topProduct?.partnerOffer?.isPartnerOffer && !isSuccessPartnerOffer && !skip) return null;

  if (topProduct?.isTpoOffer && !topProduct?.forceCampaign) {
    errorManager.handleError(ErrorService.getSeverityErrorHigh(), {
      title: 'Ops',
      message: 'Si è verificato un problema. Riprovare in seguito',
      fullscreen: true,
      disableCloseModalOnClickAction: true,
    });
    return null;
  }

  const enableLiteUI = product?.enableLiteUI || false;
  const TopNavigationComponent = enableLiteUI ? TopNavigationLite : TopNavigation;

  return (
    <>
      <TopNavigationComponent
        {...props}
        onSetModal={(modalObj) => setModal(modalObj)}
        onShowCoverageTool={(isShow) => setIsShowCoverageTool(isShow)}
        isShowCoverageTool={isShowCoverageTool}
      />
      <VfModal
        height={563}
        isOpen={modal.show}
        handleClose={resetModal}
        customFooter={modal.type === IType.PRODUCT ? modalCustomFooter : undefined}
      >
        {modal.text ? (
          modalSimpleText()
        ) : (
          <ProductDetail
            trackingOpt={{
              event: ['ui_interaction'],
              event_label: 'offer details',
              opts: { event_category: 'users', event_action: 'click' },
              visitorTrackingOpts: SoftManagerService(queryClient).getUser(),
              disableInitialTrack: false,
            }}
            details={{
              title: product.title,
              conditions: product.conditions,
              callMeNowHelp: product.callMeNowHelp,
              description: product?.descriptionPdp || product?.description,
              offerDetails: product?.offerDetails,
              customDescriptionCVM: product.customDescriptionCVM,
              price: product.price,
              recurrence: product.recurrence,
            }}
            modalInfo={{
              title: supportModulesCms?.supportmodules?.needHelp?.title || '',
              ctaName: supportModulesCms?.supportmodules?.needHelp?.ctaSupportCall || '',
            }}
            showHelps={false}
            product={product}
          />
        )}
      </VfModal>
      {product?.price && buttonAction?.title && (
        <StickyOfferContainerTopProduct>
          <StickyOffer
            slideBanner={directionSlideStickyOffer}
            id="sticky-offer-product"
            title={productOfferTitle}
            description={detailLabel || ''}
            topLabel={product?.topLabelStickyPdp || ''}
            isLoadingGoNext={checkIsLoadingActivation()}
            ctaActivation={getPDPMainAction()}
            secondCta={getPDPSecondAction()}
            content={
              <ProductDetail
                trackingOpt={{
                  event: ['view'],
                  event_label: 'offer details',
                  opts: {
                    product_target_segment: 'consumer',
                    page_section: `${getProductCategory(product.offerType || '')} offers`,
                    page_subsection: 'product_page',
                  },
                  pageProduct: {
                    product_id: `${
                      product?.isLandingClub
                        ? product?.campaignIdLanding || product?.offerId
                        : product?.offerId
                    }`,
                    product_name: product?.slug,
                    product_category: getProductCategory(product?.offerType || ''),
                    product_price: product?.price && removeCurrency(product.price),
                    product_quantity: '1',
                  },
                }}
                details={{
                  title: product.title,
                  conditions: product.conditions,
                  callMeNowHelp: product.callMeNowHelp,
                  description: product?.descriptionPdp || product?.description,
                  offerDetails: product?.offerDetails,
                  originalPrice: product.originalPrice,
                  price: product.price,
                  customDescriptionCVM: product.customDescriptionCVM,
                  recurrence: product.recurrence,
                }}
                modalInfo={{
                  title: supportModulesCms?.supportmodules?.needHelp?.title || '',
                  ctaName: supportModulesCms?.supportmodules?.needHelp?.ctaSupportCall || '',
                }}
                product={product}
              />
            }
            hideDetailLabel={product?.buttonHide}
            showDetailLabel={product?.buttonShow}
            enableLiteUI={enableLiteUI}
            disableUpperCase={props?.disableUpperCase}
          />
        </StickyOfferContainerTopProduct>
      )}
      {product && (
        <CoverageToolModal
          backgroundImage={product.image}
          isOpen={isShowCoverageTool}
          product={product}
          handleClose={hideCoverageTool}
        />
      )}
    </>
  );
};
export default TopProduct;

